import React, { useState } from 'react'
import {
  Paper, Typography, Box, Button, Alert, CircularProgress,
  Tabs, Tab, Chip, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  getRestaurantOwedRiders,
  getRestaurantPayoutRequests,
  markRestaurantPayoutPaid,
  cancelRestaurantPayout,
} from '../apollo/mutations'
import { driverPayTheme, DP, focusRing } from './driverPayTheme'

const OWED = gql`${getRestaurantOwedRiders}`
const REQS = gql`${getRestaurantPayoutRequests}`
const MARK_PAID = gql`${markRestaurantPayoutPaid}`
const CANCEL = gql`${cancelRestaurantPayout}`

// Neutral pills; red is kept for a failed or rejected payout.
const STATUS_COLORS = {
  FAILED: { bg: '#FEECEC', color: '#B91C1C' },
  REJECTED: { bg: '#FEECEC', color: '#B91C1C' },
}

// ─── Presentation ───
const PAGE = {
  minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }, bgcolor: DP.page, color: DP.text,
  fontFamily: DP.font, fontSize: 15, lineHeight: 1.45
}
const content = maxWidth => ({
  maxWidth, mx: 'auto', boxSizing: 'content-box',
  px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: { xs: 5, md: 7 },
  display: 'flex', flexDirection: 'column', gap: 3
})
const PAGE_TITLE = { fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: DP.text }
const PAGE_DESC = { fontSize: 15, lineHeight: 1.5, color: DP.body, mt: '6px', maxWidth: 820 }
// Segmented control. The grey track sits on the flex container, not the Tabs
// root, so the scrollable Tabs can still measure its own overflow. Labels are
// shown in sentence case: lowercase, then the first letter back up.
const TABS = {
  minHeight: 0,
  '& .MuiTabs-flexContainer': { display: 'inline-flex', gap: '4px', p: '4px', bgcolor: DP.segmentTrack, borderRadius: '12px' },
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTab-root': {
    minHeight: 44, height: 44, minWidth: 0, px: { xs: '14px', sm: '18px' }, py: 0, borderRadius: '9px',
    fontSize: { xs: 14, sm: 15 }, fontWeight: 600, letterSpacing: 0, color: DP.body, opacity: 1,
    textTransform: 'lowercase', display: 'inline-block', lineHeight: '44px', whiteSpace: 'nowrap',
    '&::first-letter': { textTransform: 'uppercase' },
    transition: 'background-color 150ms ease, color 150ms ease',
    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)' },
    '&.Mui-focusVisible': focusRing
  },
  '& .MuiTab-root.Mui-selected': { bgcolor: DP.text, color: '#FFFFFF', fontWeight: 700 },
  '& .MuiTab-root.Mui-selected:hover': { bgcolor: DP.text }
}
const LIST = { display: 'flex', flexDirection: 'column', gap: 1.5 }
const ROW = {
  p: { xs: '16px 18px', sm: '18px 22px' }, borderRadius: '14px', border: `1px solid ${DP.border}`,
  bgcolor: '#FFFFFF', boxShadow: 'none',
  display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1.5
}
const EMPTY = {
  py: 4, px: 2, textAlign: 'center', fontSize: 15, color: DP.secondary,
  borderRadius: '14px', border: `1px dashed ${DP.inputBorder}`
}
const NAME = { fontSize: 17, fontWeight: 700, lineHeight: 1.35, color: DP.text, overflowWrap: 'anywhere' }
const META = { display: 'block', fontSize: 14, lineHeight: 1.45, color: DP.secondary, fontVariantNumeric: 'tabular-nums' }
const AMOUNT = { fontSize: 26, fontWeight: 800, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ml: 'auto' }
const PILL = { height: 26, borderRadius: '999px', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', '& .MuiChip-label': { px: 1.25 } }

// Decorative initials for the driver avatar.
function initialsOf(name) {
  return String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?'
}
const Avatar = ({ name }) => (
  <Box aria-hidden sx={{
    width: 46, height: 46, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center',
    bgcolor: DP.text, color: '#FFFFFF', fontWeight: 700, fontSize: 16
  }}>
    {initialsOf(name)}
  </Box>
)

function fmtMoney(n) { return `$${parseFloat(n || 0).toFixed(2)}` }
function fmtDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function DriverPayoutRequests() {
  const restaurantId = localStorage.getItem('restaurantId')
  const [tab, setTab] = useState(0)
  const [payDialog, setPayDialog] = useState(null) // {row}
  const [notes, setNotes] = useState('')

  const { data: owedData, loading: owedLoading, refetch: refetchOwed } = useQuery(OWED, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  })

  const { data: reqData, loading: reqLoading, refetch: refetchReqs } = useQuery(REQS, {
    variables: { restaurantId, limit: 200 },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  })

  const [markPaid, { loading: marking }] = useMutation(MARK_PAID, {
    onCompleted: () => { setPayDialog(null); setNotes(''); refetchReqs(); refetchOwed() },
  })
  const [cancelReq, { loading: cancelling }] = useMutation(CANCEL, {
    onCompleted: () => { refetchReqs(); refetchOwed() },
  })

  if (!restaurantId) {
    return (
      <ThemeProvider theme={driverPayTheme}>
        <Box sx={PAGE}>
          <Box sx={content(1100)}>
            <Alert severity="error">No restaurant context. Please re-login.</Alert>
          </Box>
        </Box>
      </ThemeProvider>
    )
  }

  const owed = owedData?.restaurantOwedRiders || []
  const reqs = reqData?.restaurantPayoutRequests || []
  const pending = reqs.filter(r => r.status === 'REQUESTED')

  return (
    <ThemeProvider theme={driverPayTheme}>
      <Box sx={PAGE}>
        <Box sx={content(1100)}>
          <Box component="header">
            <Typography variant="h5" component="h1" sx={PAGE_TITLE}>Driver Payouts</Typography>
            <Typography variant="body2" sx={PAGE_DESC}>
              Tracks what you owe your staff drivers from delivery fees, base pay, mileage, hourly,
              and bonuses. Tips are paid separately by RushMyFood via Stripe and don&apos;t show here.
            </Typography>
          </Box>

          <Box>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={TABS}>
              <Tab label={`Currently Owed (${owed.length})`} />
              <Tab label={`Payout Requests (${pending.length} pending)`} />
              <Tab label="History" />
            </Tabs>
          </Box>

          {tab === 0 && (
            <Box sx={LIST}>
              {owedLoading && <CircularProgress />}
              {!owedLoading && owed.length === 0 && (
                <Typography sx={EMPTY}>
                  Nothing owed right now.
                </Typography>
              )}
              {owed.map(r => (
                <Paper key={r.riderId} elevation={0} sx={ROW}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0, flex: '1 1 240px' }}>
                    <Avatar name={r.riderName} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={NAME}>{r.riderName}</Typography>
                      {r.riderPhone && (
                        <Typography variant="caption" sx={META}>{r.riderPhone}</Typography>
                      )}
                    </Box>
                  </Box>
                  <Typography sx={{ ...AMOUNT, color: DP.danger }}>
                    {fmtMoney(r.owedAmount)}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}

          {tab === 1 && (
            <Box sx={LIST}>
              {reqLoading && <CircularProgress />}
              {!reqLoading && pending.length === 0 && (
                <Typography sx={EMPTY}>
                  No pending payout requests from your drivers.
                </Typography>
              )}
              {pending.map(r => (
                <Paper key={r._id} elevation={0} sx={ROW}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0, flex: '1 1 240px' }}>
                    <Avatar name={r.riderName} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={NAME}>{r.riderName}</Typography>
                      <Typography variant="caption" sx={META}>
                        Requested {fmtDate(r.createdAt)} · #{r._id.slice(-6)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ ...AMOUNT, color: DP.text, minWidth: 100, textAlign: 'right' }}>
                    {fmtMoney(r.amount)}
                  </Typography>
                  <Stack direction="row" sx={{ gap: 1.25, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => { setPayDialog(r); setNotes('') }}
                    >
                      Mark Paid
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => cancelReq({ variables: { payoutRequestId: r._id } })}
                      disabled={cancelling}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}

          {tab === 2 && (
            <Box sx={LIST}>
              {reqLoading && <CircularProgress />}
              {!reqLoading && reqs.length === 0 && (
                <Typography sx={EMPTY}>
                  No history yet.
                </Typography>
              )}
              {reqs.map(r => {
                const sc = STATUS_COLORS[r.status] || { bg: DP.chipBg, color: DP.body }
                return (
                  <Paper key={r._id} elevation={0} sx={{ ...ROW, display: 'block' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0, flex: '1 1 240px' }}>
                        <Avatar name={r.riderName} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={NAME}>{r.riderName}</Typography>
                          <Typography variant="caption" sx={META}>
                            #{r._id.slice(-6)} · Requested {fmtDate(r.createdAt)}
                            {r.paidAt && ` · Paid ${fmtDate(r.paidAt)}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip label={r.status} size="small" sx={{ ...PILL, bgcolor: sc.bg, color: sc.color }} />
                      <Typography sx={{ ...AMOUNT, ml: 0, color: DP.text, minWidth: 90, textAlign: 'right' }}>
                        {fmtMoney(r.amount)}
                      </Typography>
                    </Box>
                    {r.notes && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, pl: { sm: '60px' }, fontSize: 14, color: DP.secondary, fontStyle: 'italic' }}>
                        Notes: {r.notes}
                      </Typography>
                    )}
                  </Paper>
                )
              })}
            </Box>
          )}
        </Box>

        <Dialog open={!!payDialog} onClose={() => setPayDialog(null)} maxWidth="xs" fullWidth
          sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(15, 23, 42, 0.62)' } }}
          PaperProps={{ sx: { borderRadius: '18px', bgcolor: '#FFFFFF', backgroundImage: 'none', color: DP.text, fontFamily: DP.font, boxShadow: '0 24px 60px rgba(15,23,42,0.35)' } }}>
          <DialogTitle sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.3, color: DP.text, pt: 3 }}>Mark Payout Paid</DialogTitle>
          <DialogContent>
            {payDialog && (
              <>
                <Typography sx={{ mb: 2, fontSize: 15, color: DP.text }}>
                  Pay <strong>{payDialog.riderName}</strong> <strong>{fmtMoney(payDialog.amount)}</strong>.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2.25, fontSize: 14, lineHeight: 1.5, color: DP.body }}>
                  Confirm only after you&apos;ve actually sent the money (cash, Zelle, bank transfer, etc.).
                  Optionally add a reference so you remember how you paid.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Payment notes (optional)"
                  placeholder="e.g. Sent Zelle to 555-xxxx · ref ABC123"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                  multiline
                  rows={2}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1.25, borderTop: `1px solid ${DP.border}`, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
            <Button onClick={() => setPayDialog(null)} disabled={marking}>Cancel</Button>
            <Button
              variant="contained"
              disabled={marking}
              onClick={() => markPaid({ variables: { payoutRequestId: payDialog._id, notes: notes || null } })}
            >
              {marking ? 'Saving…' : 'Confirm Paid'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  )
}
